import { createContent, getContentCms, updateContent } from 'app/crud/contents.crud';
import { postTags } from 'app/crud/tags.crud';
import { getMetadataArray } from 'app/services/metadata.service';
import store from 'app/store/store';
import { isString } from 'lodash';

export function createContentService(data) {
  const params = getDataCreate(data);

  return createContent(params);
}

export async function updateContentService(data) {
  const newData = await getTags(data);
  const params = getDataUpdate(newData);

  return updateContent(params);
}

export function parseFieldData(data) {
  return isString(data) ? JSON.parse(data) : data;
}

export function getMimeType(content, typesContents = []) {
  const { entities } = store.getState();
  const types = typesContents.length ? typesContents : entities.types.data;
  const find = types.find((item) => item.guid === content?.type_guid);

  if (find) {
    const format = find.formats.map((item) => item.format);
    const mimeType = find.formats.map((item) => item['mime-type']);

    return [format, mimeType];
  }

  return [[], []];
}

export function getNameFileContent(content, file) {
  const find = CONTENTS_XOD.find((item) => item === content?.type_guid);
  const extensionFile = file.name.split('.').pop();
  return find ? `${content?.guid}.${extensionFile}` : undefined;
}

export const TYPES_CONTENTS = {
  CTTY_LEMONADE: 'CTTY_14',
  CTTY_LTI: 'CTTY_LTI',
  CTTY_MINTBLOCK: 'CTTY_MINTBLOCK',
  CTTY_TASK: 'CTTY_14',
  CTTY_VIDEOLESSON: 'CTTY_VIDEOLESSON',
  FIXED_LAYOUT: 'CTTY_01',
  HTML_interactive: 'CTTY_08',
  LDR_MINT: 'CTTY_02',
  MINT_PAGE: 'MINT_PAGE',
  MINT_PAGE_DUAL: 'MINT_PAGE_DUAL',
  OFFICE: 'CTTY_12',
  PDF: 'CTTY_05',
  ZIP: 'CTTY_13',
  app: 'CTTY_18',
  audio: 'CTTY_04',
  dependency: 'CTTY_00',
  image: 'CTTY_07',
  link: 'CTTY_06',
  lti: 'CTTY_LTI',
  question: 'CTTY_14',
  scorm: 'CTTY_10',
  test: 'CTTY_09',
  video: 'CTTY_03',
  videolesson: 'CTTY_VIDEOLESSON',
  xApi: 'CTTY_20',
};

export const MATERIAL_CONTENT_TYPES = [
  TYPES_CONTENTS.audio,
  TYPES_CONTENTS.FIXED_LAYOUT,
  TYPES_CONTENTS.HTML_interactive,
  TYPES_CONTENTS.image,
  TYPES_CONTENTS.link,
  TYPES_CONTENTS.OFFICE,
  TYPES_CONTENTS.PDF,
  TYPES_CONTENTS.video,
  TYPES_CONTENTS.xApi,
  TYPES_CONTENTS.ZIP,
];

export const CONTENTS_XOD = ['CTTY_01', 'CTTY_05', 'CTTY_12'];
export const STORAGE_STATUS = { uploadAccepted: 'upload-accepted', uploadDone: 'upload-done', xodStarted: 'xod-started' };
export const VIDEOLESSON_CONTENT_DEFAULT = {
  id: '',
  url: '',
  thumbnailUrl: '',
  duration: 0,
  title: '',
  type: '',
  sections: [],
  references: [],
  questions: {},
  preferences: { allowSkipAhead: true, allowRepeatQuestion: true },
};

export const CONTENTS_NOT_ACCEPT_VIEWER = [TYPES_CONTENTS.CTTY_LEMONADE, TYPES_CONTENTS.app, TYPES_CONTENTS.test, TYPES_CONTENTS.CTTY_VIDEOLESSON];
export const CONTENTS_ACCEPT_TEMPLATE_COMPONENT = [TYPES_CONTENTS.FIXED_LAYOUT, TYPES_CONTENTS.PDF];

// ---------------------------------------------------------------
// Auxiliary functions
// ---------------------------------------------------------------

function getDataCreate(data) {
  return {
    description: data.description,
    erp_id: data.erp_id ? data.erp_id : null,
    guid: data.guid,
    is_available_offline: data.is_available_offline ? 1 : 0,
    is_public: data.is_public,
    is_teacher_only: data.is_teacher_only,
    langs: data.langs ? getMetadataArray(data, 'langs') : [],
    mobile_friendly: data.mobile_friendly,
    name: data.name,
    type_guid: data.type_guid,
  };
}

function normalizeDataForTest(data) {
  if (data.type_guid !== TYPES_CONTENTS.test || !data.data?.questions) {
    return data.data;
  }

  return {
    ...data.data,
    questions: data.data.questions.map((item) => ({
      guid: item.content_guid || item.question_guid || item.guid,
      ranking_scale: item.ranking_scale,
    })),
  };
}

function getDataUpdate(data) {
  return {
    data: normalizeDataForTest(data),
    description: data.description,
    erp_id: data.erp_id ? data.erp_id : null,
    guid: data.guid,
    is_public: data.is_public,
    is_teacher_only: data.is_teacher_only,
    mobile_friendly: data.mobile_friendly,
    name: data.name,
    question_type_guid: data.question_type_guid,
    status: data.status ? 'active' : 'inactive',
    thumbnail: data.thumbnail ? data.thumbnail : undefined,
    type_guid: data.type_guid,
    url: getAttributeUrl(data),
    question_status: data.question_status || undefined,
    transcription_url: data.transcription_url,
    encoded_transcription_url: data.encoded_transcription_url,
    transcription_bundle: data.transcription_bundle,
    is_downloadable: data.is_downloadable ? 1 : 0,
    is_available_offline: data.is_available_offline ? 1 : 0,

    // Metadata array:
    collections: data.collections ? getMetadataArray(data, 'collections') : [],
    country_guid: data.country ? data.country.value : undefined,
    customTags: data.customTags ? getMetadataArray(data, 'customTags') : [],
    dependencies: data.dependencies ? getMetadataArray(data, 'dependencies') : [],
    didacticTypes: data.didacticTypes
      ? (Array.isArray(data.didacticTypes) ? data.didacticTypes.map((item) => item.value) : [data.didacticTypes.value]).filter(Boolean)
      : undefined,
    disciplines: data.disciplines ? getMetadataArray(data, 'disciplines') : [],
    educationLevels: data.educationLevels ? getMetadataArray(data, 'educationLevels') : [],
    educationYears: data.educationYears ? getMetadataArray(data, 'educationYears') : [],
    langs: data.langs ? getMetadataArray(data, 'langs') : [],
    learningObjectives: data.learningObjectives ? getMetadataArray(data, 'learningObjectives') : [],
    publications: data.publications ? getMetadataArray(data, 'publications') : [],
    tags: data.tags ? getMetadataArray(data, 'tags') : [],
    topics: data.topics ? getMetadataArray(data, 'topics') : [],
  };
}

function getAttributeUrl(data) {
  // En el caso de ficheros no hay que añadir el atributo "url" en los Update porque crea una distorsion en el bulder del fileStore
  const [format, mimeType] = getMimeType(data);
  const isUpdateUrl = !mimeType.length || !format.length;

  return isUpdateUrl ? data.url : undefined;
}

export async function getTags(data) {
  try {
    const tags = data.tags.filter((item) => !item?.__isNew__);
    const tagsNew = data.tags.filter((item) => item.__isNew__).map((item) => item.value);

    if (tagsNew.length) {
      const response = await postTags(tagsNew);
      return { ...data, tags: [...tags, ...response.data.data.tag.map((item) => ({ ...item, value: item.guid }))] };
    }

    return { ...data, tags };
  } catch (error) {
    return data;
  }
}

// ---------------------------------------------------------------
// Logic for the UI
// ---------------------------------------------------------------

export function hasThumbnail(content) {
  return (
    content.type_guid === TYPES_CONTENTS.FIXED_LAYOUT ||
    content.type_guid === TYPES_CONTENTS.PDF ||
    content.type_guid === TYPES_CONTENTS.xApi ||
    content.type_guid === TYPES_CONTENTS.video
  );
}

export function hasContentUpload({ content, mimeTypes }) {
  // Si tiene mimeTypes indica que es un fichero, por lo que se debe mostrar el formulario de subida de fichero
  return !!mimeTypes.length && content.type_guid !== TYPES_CONTENTS.CTTY_LEMONADE;
}

// Traer el contenido completo del CMS
export async function getContentCmsService(params) {
  try {
    const response = await getContentCms(params);
    return response?.data?.data;
  } catch (error) {
    console.error('Error al obtener contenido CMS:', error);
    return null;
  }
}
